// @file Helper functions for dashboard.

import { galleryTextArray } from '@@/bits/collections_helper'
import { __ } from '@@/bits/intl'
import type { UrlOptions } from '@@/bits/location'
import { getPathnameItems, getSearchParams, transformCurrentUrl } from '@@/bits/location'
import { safeLocalStorage } from '@@/bits/safe_storage'
import { LibraryType } from '@@/enums'
import type { Library, LibraryId, UserId, WallCamelCase as Wall, WallViz } from '@@/types'
import type { WallsFilter } from '@@/types/collections'
import { CollectionKeyTypes } from '@@/types/collections'
import type { Audience, DashView } from '@@/types/dash'
import { ClassTabs, MobilePage } from '@@/types/dash'

function dashViewUrlParams(dashView: DashView): Record<string, string> {
  const { collectionKey, filter, gallery, mobilePage, page, tab } = dashView
  // The empty strings indicate that we want to remove those fields.
  const params: Record<string, string> = {
    page: page ?? '',
    mobile_page: mobilePage ?? '',
    filter: filter ?? '',
    gallery: gallery ?? '',
    tab: tab ?? '',
    folder_id: '',
    library_id: '',
  }

  if (collectionKey !== undefined && collectionKey !== null) {
    const { typeKey, indexKey } = collectionKey
    if (typeKey === CollectionKeyTypes.FolderId) {
      params.folder_id = String(indexKey)
    } else if (typeKey === CollectionKeyTypes.GroupFolderId) {
      params.group_id = String(indexKey)
    } else if (typeKey === CollectionKeyTypes.Filter) {
      params.filter = indexKey
    }
  }

  if (filter !== undefined) {
    params.filter = filter
  }

  if (gallery !== undefined) {
    params.gallery = gallery
  }

  if (tab !== undefined) {
    params.tab = tab
  }

  return params
}

function dashViewUrl(dashView: DashView, urlOptions?: UrlOptions): string {
  const searchParams = dashViewUrlParams(dashView)
  if (urlOptions?.searchParams != null) {
    Object.assign(searchParams, urlOptions.searchParams)
  }
  return transformCurrentUrl({ withQueryString: false }, { searchParams, path: urlOptions?.path ?? '/dashboard' })
}

function dashMakeUrl(prefixPath: string): string {
  return dashViewUrl({ mobilePage: MobilePage.LayoutPicker }, { path: `${prefixPath}/make` })
}

const CrossLibraryToFilterMap = {
  recents: 'combined_recents',
  bookmarks: 'favorites',
  imports: 'imports',
  shared: 'combined_shared',
}

const TextToVizMap = {
  all: 'all',
  wall: 'grid',
  stream: 'stream',
  grid: 'matrix',
  shelf: 'shelf',
  map: 'map',
  canvas: 'free',
  timeline: 'timeline',
}

function getDashPathnameParams(): Record<string, string> {
  const pathnameItems = getPathnameItems()
  if (pathnameItems[0] !== 'dashboard') {
    return {}
  }

  const crossLibraryArray = ['bookmarks', 'recents', 'imports', 'shared']
  // path is /dashboard/:crossLibraryCollection
  if (crossLibraryArray.includes(pathnameItems[1])) {
    return { filter: CrossLibraryToFilterMap[pathnameItems[1]] as WallsFilter }
  }

  // path is /dashboard/gallery/:viz
  if (pathnameItems[1] === 'gallery') {
    if (galleryTextArray.includes(pathnameItems[2]) && pathnameItems[2] !== undefined) {
      return { gallery: TextToVizMap[pathnameItems[2]] }
    }

    // fallback to all if viz is incorrectly typed or not provided
    return { gallery: 'all' }
  }

  // path is /dashboard/make
  if (pathnameItems[1] === 'make') {
    return { make: 'make' }
  }

  // path is /dashboard/learn
  if (pathnameItems[1] === 'learn') {
    return { learn: 'learn' }
  }

  // path is /dashboard/<LibraryType>/:librarySlug(/:tab)
  if (Object.values(LibraryType).includes(pathnameItems[1] as LibraryType) && pathnameItems[2] !== undefined) {
    // path is /dashboard/school/:schoolSlug/class/:classSlugWithHashId
    if (pathnameItems[3] === 'class' && pathnameItems[4] !== undefined) {
      return { librarySlug: pathnameItems[2], classSlugWithHashId: pathnameItems[4] }
    }
    // path is /dashboard/school/:schoolSlug/discussion/:discussionHashId
    if (pathnameItems[3] === 'discussion' && pathnameItems[4] !== undefined) {
      return { librarySlug: pathnameItems[2], discussionHashId: pathnameItems[4] }
    }
    if (pathnameItems[3] === 'gallery') return { librarySlug: pathnameItems[2], gallery: 'all' }
    if (pathnameItems[3] === 'learn') return { librarySlug: pathnameItems[2], learn: 'learn' }
    if (pathnameItems[3] === 'make') return { librarySlug: pathnameItems[2], make: 'make' }
    return { librarySlug: pathnameItems[2] }
  }

  return {}
}

function getDashViewFromUrlParams(urlParams: Record<string, string> | null = null): DashView {
  const searchParams = urlParams ?? getSearchParams()
  const pathnameParams = getDashPathnameParams()
  const librarySlug = pathnameParams.librarySlug
  const classSlugWithHashId = pathnameParams.classSlugWithHashId
  const discussionHashId = (pathnameParams as Record<string, string | undefined>).discussionHashId
  const folderId = searchParams.folder_id
  let groupId = searchParams.group_id
  const filter = pathnameParams.filter ?? searchParams.filter
  const gallery = pathnameParams.gallery ?? searchParams.gallery
  const learn = pathnameParams.learn ?? searchParams.learn
  const make = pathnameParams.make ?? searchParams.make
  const tab = searchParams.tab
  const mobilePage = searchParams.mobile_page
  const page = searchParams.page
  const create = searchParams.create
  const audience = searchParams.audience

  // For campus class pages we extract the numeric group ID from the class hashid
  // since the group_id query param is not set in the URL for campus classes
  // For example, the class slug segment, "slug-HASHID", might look like: "ap-lang-9MmEePDA5DYQdBxk"
  const isCampusClass =
    librarySlug !== undefined && librarySlug !== '' && classSlugWithHashId !== undefined && classSlugWithHashId !== ''
  if (isCampusClass) {
    const hashid = classSlugWithHashId.split('-').pop()
    if (hashid) {
      const vueStartingState = (window as any).ww?.vueStartingState
      const classGroups = vueStartingState?.classGroups ?? []
      const matchingClass = classGroups.find((classGroup: any) => classGroup.hashid === hashid)
      if (matchingClass) {
        groupId = String(matchingClass.id)
      }
    }
  }

  const dashView: DashView = {}

  if (page !== undefined && page !== '') {
    dashView.page = page
  } else if (create !== undefined && create !== '') {
    dashView.page = 'layoutPicker'
  }

  if (mobilePage !== undefined && mobilePage !== '') {
    dashView.mobilePage = mobilePage as MobilePage
  }

  if (librarySlug !== undefined && librarySlug !== '') {
    dashView.librarySlug = librarySlug
    dashView.mobilePage = MobilePage.CollectionsMenu

    // If the librarySlug and filter is exist, the mobilePage should be collection
    if (filter !== undefined && filter !== '') {
      dashView.mobilePage = MobilePage.Collection
    }
  }

  if (classSlugWithHashId !== undefined && classSlugWithHashId !== '') {
    dashView.classSlugWithHashId = classSlugWithHashId
    dashView.mobilePage = MobilePage.Collection
  }

  // Map discussion path segment to discussionHashId on DashView
  if (discussionHashId !== undefined && discussionHashId !== '') {
    dashView.discussionHashId = discussionHashId
  }

  if (folderId !== undefined && folderId !== '') {
    dashView.collectionKey = { typeKey: CollectionKeyTypes.FolderId, indexKey: Number(folderId) }
  } else if (groupId !== undefined && groupId !== '') {
    dashView.collectionKey = { typeKey: CollectionKeyTypes.GroupFolderId, indexKey: Number(groupId) }
  } else if (filter !== undefined && filter !== '') {
    dashView.filter = filter as WallsFilter
  }

  if (gallery !== undefined && gallery !== '') {
    dashView.gallery = gallery as WallViz
  }

  if (audience !== undefined && audience !== '') {
    dashView.audience = audience as Audience
  }

  if (make !== undefined && make !== '') {
    dashView.make = make as 'make'
  }

  if (learn !== undefined && learn !== '') {
    dashView.learn = learn as 'learn'
  }

  if (tab !== undefined && tab !== '') {
    dashView.tab = tab as ClassTabs
  }

  // If a cross library filter exist, a mobile_page must be set
  // else the user will not be able to view the page in mobile directly
  if (dashView.mobilePage === undefined && dashView.filter !== undefined) {
    switch (dashView.filter) {
      case CrossLibraryToFilterMap.bookmarks:
      case CrossLibraryToFilterMap.imports:
        dashView.mobilePage = MobilePage.CollectionsMenu
        break
      case CrossLibraryToFilterMap.recents:
      case CrossLibraryToFilterMap.shared:
        dashView.mobilePage = MobilePage.Collection
        break
    }
  }

  // If a gallery param exist, a mobile_page must be set
  // else the user will not be able to view the page in mobile directly
  if (dashView.mobilePage === undefined && dashView.gallery !== undefined) {
    switch (dashView.gallery) {
      case 'all': // Special case for 'all' since it is used for the collections menu page
        dashView.mobilePage = MobilePage.CollectionsMenu
        break
      default:
        dashView.mobilePage = MobilePage.Collection
        break
    }
  }
  return dashView
}

function saveDashViewUrlIntoStorage(): void {
  const url = new URL(window.location.href)
  // These apply once and aren't part of the dashboard view: prompt is a create action, grade and subject Gallery filters.
  url.searchParams.delete('prompt')
  url.searchParams.delete('grade')
  url.searchParams.delete('subject')
  safeLocalStorage.setItem('dashViewUrl', url.toString())
}

function getDashViewUrlFromStorage(): string | null {
  return safeLocalStorage.getItem('dashViewUrl')
}

function clearDashViewUrlFromStorage(): void {
  safeLocalStorage.removeItem('dashViewUrl')
}

/**
 * Params that indicate explicit navigation intent and should not be overwritten by storage restoration.
 */
const EXPLICIT_DASH_PARAMS = {
  BASE: ['audience', 'folder_id', 'filter', 'prompt', 'grade', 'subject'] as const,
  CAMPUS: ['tab'] as const,
} as const

/**
 * Check if the current URL has explicit params that should be preserved (not overwritten by storage).
 * @param additionalParams - Additional param names to check (e.g., 'tab' for campus class pages)
 */
function hasExplicitDashParams(additionalParams: string[] = []): boolean {
  const currentUrlParams = new URLSearchParams(window.location.search)
  const paramsToCheck = [...EXPLICIT_DASH_PARAMS.BASE, ...additionalParams]
  return paramsToCheck.some((param) => currentUrlParams.has(param))
}

/**
 * Attempt to restore URL search params from localStorage.
 * @param additionalExplicitParams - Additional param names that indicate explicit navigation (e.g., 'tab')
 */
function tryRestoreUrlFromStorage(additionalExplicitParams: string[] = []): void {
  const dashViewUrlFromStorage = getDashViewUrlFromStorage()

  if (dashViewUrlFromStorage === null) return

  const currentUrl = new URL(window.location.href)
  const urlFromStorage = new URL(dashViewUrlFromStorage)

  // If pathname is not the same, don't restore (e.g., don't restore school page params on class page)
  if (urlFromStorage.pathname !== currentUrl.pathname) return

  // If the search params are already the same, nothing to do
  if (urlFromStorage.search === currentUrl.search) return

  // Don't restore if current URL already has explicit params that should be preserved
  if (hasExplicitDashParams(additionalExplicitParams)) return

  currentUrl.search = urlFromStorage.search
  window.history.pushState(null, '', currentUrl.toString())
  saveDashViewUrlIntoStorage()
}

// The only fields these bits read, so tests can build fixtures without casting.
type FilterableWall = Pick<Wall, 'libraryId'> & { builder: Pick<Wall['builder'], 'id'> }
type WorkspaceLibrary = Pick<Library, 'name' | 'avatar' | 'defaultAvatar' | 'libraryType'>

/** The workspace a padlet lives in, named for display under a wall card or on a filter pill. */
interface WallWorkspace {
  key: string
  name: string
  logo?: string
  fallbackLogo?: string
  libraryId: LibraryId | null
}

/** A workspace the user has recent padlets in, as one filter pill. */
interface WallWorkspaceFilter extends WallWorkspace {
  count: number
}

/** The personal bucket: a padlet the user made, in their own solo library or in no library at all. */
const PERSONAL_WORKSPACE_KEY = 'personal'

/** Everything the user cannot be shown a workspace name for: non-member libraries, and padlets they did not make. */
const OTHER_WORKSPACE_KEY = 'other'

/** Buckets a wall by workspace. Solo libraries merge into personal; anything unnameable into 'other'. */
function wallWorkspaceKey(
  libraryId: LibraryId | null,
  librariesById: Record<LibraryId, WorkspaceLibrary>,
  isMadeByCurrentUser: boolean,
): string {
  // A padlet with no library is one of the two personal forms, so it skips the library lookup entirely.
  if (libraryId !== null) {
    const library = librariesById[libraryId]
    // undefined means the library exists but the user is not a member, so it cannot be named.
    if (library === undefined) return OTHER_WORKSPACE_KEY
    // A solo library is the user's own personal space, so it merges rather than standing on its own.
    if (library.libraryType !== LibraryType.Solo) return `library-${libraryId}`
  }
  // Recents holds padlets other people made and shared, and those are not the user's personal space.
  return isMadeByCurrentUser ? PERSONAL_WORKSPACE_KEY : OTHER_WORKSPACE_KEY
}

/** Names and illustrates the workspace a wall lives in. Personal and 'other' have no logo. */
function wallWorkspace(
  libraryId: LibraryId | null,
  librariesById: Record<LibraryId, WorkspaceLibrary>,
  isMadeByCurrentUser: boolean,
): WallWorkspace {
  const key = wallWorkspaceKey(libraryId, librariesById, isMadeByCurrentUser)
  if (key === PERSONAL_WORKSPACE_KEY) return { key, name: __('Personal'), libraryId: null }
  if (key === OTHER_WORKSPACE_KEY) return { key, name: __('Other padlets'), libraryId: null }

  // Any other key came from a library present in the map, so this lookup cannot miss.
  const library = librariesById[libraryId as LibraryId]
  return { key, name: library.name, logo: library.avatar, fallbackLogo: library.defaultAvatar, libraryId }
}

/** The user's own space opens the row and the padlets that are nobody's workspace close it. */
const PINNED_PILL_RANK_BY_KEY: Record<string, number> = { [PERSONAL_WORKSPACE_KEY]: 0, [OTHER_WORKSPACE_KEY]: 2 }

function workspacePillRank(key: string): number {
  // Libraries sit between the two pinned buckets on one shared rank, so a stable sort leaves them in order.
  return PINNED_PILL_RANK_BY_KEY[key] ?? 1
}

/**
 * One filter pill per workspace present in `walls`: personal, then the libraries by first appearance, then other.
 * Callers pass walls already sorted by recency, so that library order is most-recent-first.
 */
function wallWorkspaceFilters(
  walls: FilterableWall[],
  librariesById: Record<LibraryId, WorkspaceLibrary>,
  currentUserId: UserId,
): WallWorkspaceFilter[] {
  const filtersByKey = new Map<string, WallWorkspaceFilter>()

  walls.forEach((wall) => {
    // Sharing wallWorkspace keeps a pill and a card's workspace row named identically.
    const workspace = wallWorkspace(wall.libraryId, librariesById, wall.builder.id === currentUserId)

    const existing = filtersByKey.get(workspace.key)
    if (existing !== undefined) {
      existing.count += 1
      return
    }

    filtersByKey.set(workspace.key, { ...workspace, count: 1 })
  })

  return Array.from(filtersByKey.values()).sort((a, b) => workspacePillRank(a.key) - workspacePillRank(b.key))
}

/** Keeps only the walls belonging to one workspace pill. */
function wallsInWorkspace<T extends FilterableWall>(
  walls: T[],
  workspaceKey: string,
  librariesById: Record<LibraryId, WorkspaceLibrary>,
  currentUserId: UserId,
): T[] {
  return walls.filter(
    (wall) => wallWorkspaceKey(wall.libraryId, librariesById, wall.builder.id === currentUserId) === workspaceKey,
  )
}

export {
  dashViewUrlParams,
  dashViewUrl,
  dashMakeUrl,
  getDashPathnameParams,
  getDashViewFromUrlParams,
  saveDashViewUrlIntoStorage,
  getDashViewUrlFromStorage,
  clearDashViewUrlFromStorage,
  EXPLICIT_DASH_PARAMS,
  hasExplicitDashParams,
  tryRestoreUrlFromStorage,
  wallWorkspace,
  wallWorkspaceKey,
  wallWorkspaceFilters,
  wallsInWorkspace,
  PERSONAL_WORKSPACE_KEY,
  OTHER_WORKSPACE_KEY,
  type WorkspaceLibrary,
  type WallWorkspace,
  type WallWorkspaceFilter,
}
